// src/components/QuienesSomos.jsx
import React, { forwardRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Target, Eye, Columns3 } from "lucide-react";

const QuienesSomos = forwardRef((props, ref) => {
  const [activePopup, setActivePopup] = useState(null);

  const popups = {
    Mision: {
      title: "Misión",
      icon: <Target className="w-[clamp(3rem,6vw,4rem)] h-[clamp(3rem,6vw,4rem)] text-white" />,
      iconWhite: <Target className="w-[clamp(3rem,6vw,4rem)] h-[clamp(3rem,6vw,4rem)] text-white" />,
      content: `Nos especializamos en ordenar, optimizar y profesionalizar la gestión para que las organizaciones puedan enfocarse en lo que realmente importa: su desarrollo y sostenibilidad. Trabajamos cerca de cada empresa, entendiendo su realidad, diseñando soluciones prácticas y aplicando metodologías que generan resultados medibles y duraderos.`,
    },
    Vision: {
      title: "Visión",
      icon: <Eye className="w-[clamp(3rem,6vw,4rem)] h-[clamp(3rem,6vw,4rem)] text-white" />,
      iconWhite: <Eye className="w-[clamp(3rem,6vw,4rem)] h-[clamp(3rem,6vw,4rem)] text-white" />,
      content: `Ser el aliado estratégico elegido por las PyMEs que buscan crecer de forma sostenible, destacándonos por traducir la visión en planes ejecutables, con seguimiento e indicadores que aseguren resultados medibles y equipos de alto rendimiento.`,
    },
    Pilares: {
      title: "Nuestros Pilares",
      icon: <Columns3 className="w-[clamp(3rem,6vw,4rem)] h-[clamp(3rem,6vw,4rem)] text-white" />,
      iconWhite: <Columns3 className="w-[clamp(3rem,6vw,4rem)] h-[clamp(3rem,6vw,4rem)] text-white" />,
      content: ["Mirada Integral", "Acompañamiento Real", "Soluciones Prácticas", "Mejora Contínua"],
    },
  };

  return (
    <section
      ref={ref}
      className="pt-20 pb-16 px-6 sm:px-8 bg-[#D1D1CF] text-primary relative overflow-hidden"
    >
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-gray-400 to-transparent"></div>

      <div className="max-w-6xl mx-auto text-center">
        <h2 className="text-4xl md:text-5xl font-bold mb-10">
          Quiénes Somos
        </h2>

        <div className="mb-12 text-lg text-justify" style={{ color: "#120B3E" }}>
          <p className="mb-4">
            En <span className="font-semibold">Core Solutions</span> creemos que las
            empresas crecen cuando las personas que las forman crecen también. Somos
            un equipo de profesionales multidisciplinarios que combina experiencia,
            visión estratégica y compromiso para acompañar a cada cliente en su
            proceso de transformación.
          </p>
          <p className="mb-4">
            Quienes nos eligen destacan nuestra capacidad de escucha, nuestra mirada
            integral y el hecho de que nos involucramos de verdad. No solo analizamos
            los problemas: nos comprometemos a resolverlos con metodología, empatía y
            pasión por lo que hacemos.
          </p>
          <p>
            En <span className="font-semibold">Core Solutions</span>, transformamos los
            desafíos en oportunidades y las ideas en resultados concretos.
          </p>
        </div>

        {/* Tarjetas circulares */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 sm:gap-10">
          {Object.keys(popups).map((key) => (
            <motion.div
              key={key}
              whileHover={{ scale: 1.08, y: -8 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => setActivePopup(key)}
              className="group cursor-pointer relative flex flex-col items-center justify-center rounded-full w-[clamp(10rem,18vw,14rem)] h-[clamp(10rem,18vw,14rem)] mx-auto bg-[#120B3E] shadow-[0_10px_25px_rgba(171,101,173,0.4)] transition-all duration-300 hover:shadow-[0_15px_35px_rgba(171,101,173,0.6)] overflow-hidden"
              style={{ border: "4px solid #FFFFFF" }}
            >
              <motion.div
                className="absolute inset-0 rounded-full bg-[radial-gradient(circle_at_30%_30%,rgba(255,255,255,0.2),transparent_70%)] opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                animate={{ rotate: 360 }}
                transition={{ repeat: Infinity, duration: 8, ease: "linear" }}
              ></motion.div>

              <motion.div
                animate={{ y: [0, -6, 0] }}
                transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
                className="z-10"
              >
                {popups[key].icon}
              </motion.div>

              <h3 className="text-2xl md:text-3xl font-bold text-white mt-4 z-10 drop-shadow-md">
                {popups[key].title}
              </h3>

              <div className="absolute inset-0 rounded-full bg-gradient-to-br from-[#ffffff33] to-[#AB65AD55] opacity-50 blur-2xl"></div>
            </motion.div>
          ))}
        </div>

        <p className="mt-12 text-xl md:text-2xl font-semibold text-primary">
          Nos especializamos en ordenar, optimizar y profesionalizar la gestión de las organizaciones
        </p>
      </div>

      {/* POP-UP */}
      <AnimatePresence>
        {activePopup && (
          <motion.div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="relative bg-gradient-to-br from-[#FFFFFF] to-[#F8F8FF] rounded-3xl shadow-[0_15px_40px_rgba(18,11,62,0.25)] max-w-[700px] w-full sm:w-[90%] overflow-hidden border border-[#120B3E]/20"
              initial={{ y: 60, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 60, opacity: 0 }}
              transition={{ type: "spring", damping: 18, stiffness: 100 }}
            >
              <motion.div
                className="absolute inset-0 rounded-3xl border-[2px] border-transparent bg-[conic-gradient(from_0deg,rgba(171,101,173,0.2),rgba(18,11,62,0.2),rgba(171,101,173,0.2))] opacity-30 blur-[1.5px]"
                animate={{ rotate: 360 }}
                transition={{ repeat: Infinity, duration: 14, ease: "linear" }}
              ></motion.div>

              <div className="relative bg-gradient-to-r from-[#120B3E] via-[#2B1B6B] to-[#AB65AD] px-8 py-6 flex items-center justify-between">
                <div className="flex items-center gap-4 text-white">
                  <motion.div
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ delay: 0.2 }}
                  >
                    {popups[activePopup].iconWhite}
                  </motion.div>
                  <h3 className="text-3xl md:text-4xl font-semibold tracking-wide text-center w-full">
                    {popups[activePopup].title}
                  </h3>
                </div>
                <button
                  className="text-white hover:text-white transition-colors"
                  onClick={() => setActivePopup(null)}
                >
                  <X size={36} />
                </button>
              </div>

              <div className="relative z-10 p-6 text-[#120B3E]/90 leading-relaxed text-lg md:text-xl">
                {Array.isArray(popups[activePopup].content) ? (
                  <ul className="space-y-4">
                    {popups[activePopup].content.map((valor, idx) => (
                      <motion.li
                        key={idx}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: idx * 0.1 }}
                        className="flex items-center gap-3"
                      >
                        <span className="w-4 h-4 bg-[#AB65AD] rounded-full shadow-sm"></span>
                        {valor}
                      </motion.li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-justify">{popups[activePopup].content}</p>
                )}
              </div>

              <div className="h-1 bg-gradient-to-r from-[#AB65AD] via-[#120B3E] to-[#AB65AD]"></div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-gray-400 to-transparent"></div>
    </section>
  );
});

export default QuienesSomos;
